import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import toast from "react-hot-toast";
import { useGetAddOnCatalogQuery, useUpdateAddOnPriceMutation } from "@/store/admin_api";
import { AddOnCatalogEntry } from "@/model/interface/AddOn";

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AddOnPricingAdmin() {
  const { data, isLoading } = useGetAddOnCatalogQuery();
  const [updateAddOnPrice, { isLoading: isSaving }] = useUpdateAddOnPriceMutation();

  const [editing, setEditing] = useState<AddOnCatalogEntry | null>(null);
  const [draftDollars, setDraftDollars] = useState("");

  const openEdit = (entry: AddOnCatalogEntry) => {
    setEditing(entry);
    setDraftDollars((entry.monthlyCostCents / 100).toFixed(2));
  };

  const handleSave = async () => {
    if (!editing) {
      return;
    }
    const dollars = parseFloat(draftDollars);
    if (Number.isNaN(dollars) || dollars < 0) {
      toast.error("Enter a valid monthly price");
      return;
    }
    try {
      await updateAddOnPrice({
        lookupKey: editing.lookupKey,
        monthlyCostCents: Math.round(dollars * 100),
      }).unwrap();
      toast.success(`Updated ${editing.displayName}`);
      setEditing(null);
    } catch {
      toast.error("Failed to update price in Stripe");
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Add-on Pricing</h1>
        <p className="text-sm text-muted-foreground">
          Add-ons sold on top of any plan. Editing a price creates a new Stripe price and moves the lookup key to it;
          existing subscriptions keep their current price until changed. AI voice agents also bill $0.39/min of usage.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Add-on</TableHead>
                <TableHead>Lookup key</TableHead>
                <TableHead className="text-right">Monthly price</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.map((entry) => (
                <TableRow key={entry.lookupKey}>
                  <TableCell className="font-medium">{entry.displayName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground font-mono">{entry.lookupKey}</TableCell>
                  <TableCell className="text-right">{formatDollars(entry.monthlyCostCents)}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(entry)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit price</DialogTitle>
            <DialogDescription>{editing?.displayName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="addon-price">Monthly price ($)</Label>
            <Input
              id="addon-price"
              type="number"
              min={0}
              step="0.01"
              value={draftDollars}
              onChange={(e) => setDraftDollars(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save to Stripe"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
